import {
  FP_SHOW_WHEN_ATTRIBUTE,
  FP_SHOW_WHEN_CONDITIONS,
} from "@forgepoint/shared";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuoteCustomFieldNames } from "@/store/quoteSettingsStore";
import { buildQuoteFieldOptions } from "./quoteFields";
import { SHOW_WHEN_CONDITION_LABELS, showWhenLabel } from "./showWhen";

/** Sentinel Select value for "no condition" (Radix forbids ""). */
const ALWAYS_VALUE = "always";

interface ShowWhenSelectProps {
  /** The block's current `data-fp-show-when` value, or null when unconditional. */
  showWhen: string | null;
  /** Writes (or clears, on "") one attribute on the selected block. */
  onAttributeChange: (attribute: string, value: string) => void;
}

/**
 * Picks the condition gating a block: always, a named quote condition, or any
 * quote field (standard or custom) being filled in. A block whose condition
 * does not hold is left out of the rendered document. A value the picker
 * cannot offer (a negated or `&&`-joined condition authored by hand or by the
 * agent) is kept as a one-off item so opening the picker never clobbers it.
 * @param showWhen - The block's current condition, or null
 * @param onAttributeChange - Writes or clears the attribute
 * @returns The show-when picker
 */
export function ShowWhenSelect({
  showWhen,
  onAttributeChange,
}: ShowWhenSelectProps) {
  const fieldOptions = buildQuoteFieldOptions(useQuoteCustomFieldNames());
  const isKnown =
    showWhen === null ||
    fieldOptions.some((option) => option.source === showWhen) ||
    FP_SHOW_WHEN_CONDITIONS.some((candidate) => candidate === showWhen);

  return (
    <div>
      <Label className="mb-1 block text-[11px]">Show when</Label>
      <Select
        value={showWhen ?? ALWAYS_VALUE}
        onValueChange={(value) =>
          onAttributeChange(
            FP_SHOW_WHEN_ATTRIBUTE,
            value === ALWAYS_VALUE ? "" : value,
          )
        }
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALWAYS_VALUE}>Always</SelectItem>
          {showWhen !== null && !isKnown && (
            <SelectItem value={showWhen}>{showWhenLabel(showWhen)}</SelectItem>
          )}
          <SelectGroup>
            <SelectLabel>Quote conditions</SelectLabel>
            {FP_SHOW_WHEN_CONDITIONS.map((condition) => (
              <SelectItem key={condition} value={condition}>
                {SHOW_WHEN_CONDITION_LABELS[condition]}
              </SelectItem>
            ))}
          </SelectGroup>
          <SelectGroup>
            <SelectLabel>Field is filled in</SelectLabel>
            {fieldOptions.map((option) => (
              <SelectItem key={option.source} value={option.source}>
                {option.label} is filled in
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}
